import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider } from '@material-ui/core';
import { FiHash } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import UnformTextArea from '../../newComponents/UnformTextArea';
import Button from '../../newComponents/Button';

import * as S from './styles';

const LIMIT = 20;

interface InutilizarFormData {
  serie: string;
  numeroInicio: string;
  numeroFim: string;
  justificativa: string;
}

interface Inutilizacao {
  serie: number;
  numeroInicio: number;
  numeroFim: number;
  justificativa: string;
  status?: string;
  protocolo?: string;
  data?: string;
}

const formatarData = (valor?: string): string => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleDateString('pt-BR');
};

const Inutilizacao: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [inutilizacoes, setInutilizacoes] = useState<Inutilizacao[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<{ total?: number; totalPages?: number }>({});

  const empresaId = user.empresa_id;

  /** Carrega as inutilizações registradas da empresa, paginadas (T4.7). */
  const carregarInutilizacoes = useCallback(
    async (paginaAlvo = 1) => {
      try {
        const { data } = await api.get(
          `/companies/${empresaId}/nfce/inutilizacoes`,
          { params: { page: paginaAlvo, limit: LIMIT } },
        );
        const lista: Inutilizacao[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setInutilizacoes(lista);
        setMeta(data?.meta ?? {});
        setPage(paginaAlvo);
      } catch (err) {
        // Lista é complementar; falha aqui não bloqueia o formulário.
      }
    },
    [empresaId],
  );

  useEffect(() => {
    carregarInutilizacoes(1);
  }, [carregarInutilizacoes]);

  const totalPaginas =
    meta.totalPages ||
    (meta.total ? Math.ceil(Number(meta.total) / LIMIT) : undefined);

  const handleSubmit = useCallback(
    async (data: InutilizarFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          serie: Yup.number()
            .typeError('Série deve ser um número')
            .integer('Série deve ser inteira')
            .positive('Série inválida')
            .required('Informe a série'),
          numeroInicio: Yup.number()
            .typeError('Número inicial deve ser um número')
            .integer('Número inicial deve ser inteiro')
            .positive('Número inicial inválido')
            .required('Informe o número inicial'),
          numeroFim: Yup.number()
            .typeError('Número final deve ser um número')
            .integer('Número final deve ser inteiro')
            .positive('Número final inválido')
            .min(
              Yup.ref('numeroInicio'),
              'O número final deve ser maior ou igual ao inicial',
            )
            .required('Informe o número final'),
          justificativa: Yup.string()
            .required('Informe a justificativa')
            .min(15, 'A justificativa deve ter no mínimo 15 caracteres')
            .max(255, 'A justificativa deve ter no máximo 255 caracteres'),
        });

        await schema.validate(data, { abortEarly: false });

        const payload = {
          serie: Number(data.serie),
          numeroInicio: Number(data.numeroInicio),
          numeroFim: Number(data.numeroFim),
          justificativa: data.justificativa.trim(),
        };

        const response = await api.post(
          `/companies/${empresaId}/nfce/inutilizar`,
          payload,
        );

        const resultado = response.data || {};

        addToast({
          type: 'success',
          title: 'Inutilização registrada',
          description: resultado.protocolo
            ? `Protocolo: ${resultado.protocolo}`
            : undefined,
        });

        formRef.current?.reset();
        carregarInutilizacoes(1);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else {
          const message =
            (err as any)?.response?.data?.message ||
            'Não foi possível registrar a inutilização.';
          addToast({
            type: 'error',
            title: 'Erro ao inutilizar',
            description: message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, empresaId, carregarInutilizacoes],
  );

  return (
    <S.Container>
      <div className="header">
        <h2>Inutilização de Notas</h2>
        <p>
          Registre a inutilização de faixas de numeração de NFC-e com
          justificativa (15 a 255 caracteres).
        </p>
      </div>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleSubmit}>
        <div className="multi">
          <div>
            <Input
              name="serie"
              icon={FiHash}
              label="Série:"
              type="number"
              placeholder="Ex.: 1"
              disabled={isLoading}
            />
          </div>
          <div>
            <Input
              name="numeroInicio"
              icon={FiHash}
              label="Número inicial:"
              type="number"
              placeholder="Ex.: 100"
              disabled={isLoading}
            />
          </div>
          <div>
            <Input
              name="numeroFim"
              icon={FiHash}
              label="Número final:"
              type="number"
              placeholder="Ex.: 120"
              disabled={isLoading}
            />
          </div>
        </div>
        <div>
          <UnformTextArea
            name="justificativa"
            label="Justificativa (15 a 255 caracteres):"
            placeholder="Descreva o motivo da inutilização da faixa"
            rows={3}
            maxLength={255}
            disabled={isLoading}
          />
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Inutilizar faixa
          </Button>
        </div>
      </S.CustomForm>

      <h3>Inutilizações registradas</h3>

      <S.ContainerList>
        <S.TableHeader>
          <span>Série</span>
          <span>Faixa</span>
          <span>Justificativa</span>
          <span>Status</span>
          <span>Protocolo</span>
          <span>Data</span>
        </S.TableHeader>

        {inutilizacoes.length === 0 ? (
          <S.EmptyState>
            Nenhuma inutilização registrada.
          </S.EmptyState>
        ) : (
          inutilizacoes.map((item, index) => (
            <S.TableRow key={`${item.serie}-${item.numeroInicio}-${index}`}>
              <p>{item.serie}</p>
              <p>
                {item.numeroInicio} – {item.numeroFim}
              </p>
              <p title={item.justificativa}>{item.justificativa}</p>
              <p>{item.status || '-'}</p>
              <p>{item.protocolo || '-'}</p>
              <p>{formatarData(item.data)}</p>
            </S.TableRow>
          ))
        )}
      </S.ContainerList>

      <S.Paginacao>
        <button
          type="button"
          onClick={() => carregarInutilizacoes(page - 1)}
          disabled={page <= 1}
        >
          Anterior
        </button>
        <span>
          Página {page}
          {totalPaginas ? ` de ${totalPaginas}` : ''}
        </span>
        <button
          type="button"
          onClick={() => carregarInutilizacoes(page + 1)}
          disabled={
            totalPaginas
              ? page >= totalPaginas
              : inutilizacoes.length < LIMIT
          }
        >
          Próxima
        </button>
      </S.Paginacao>
    </S.Container>
  );
};

export default Inutilizacao;
